import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getNodesBounds, useStore } from "reactflow";

import { useGetCultivationFormData } from "../../../api/farmGate";
import { normalizeCommentParentId } from "../../../api/noteDocument/noteDocument";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import NoteAndDocument from "../../../components/noteAndDocument/NoteAndDocument";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import CultivarField from "./components/CultivarField";
import FarmGateDetails from "./components/FarmGateDetails";
import Fertilizer from "./components/Fertilizer";
import Fields from "./components/Fields";
import Irrigation from "./components/Irrigation";
import LandUse from "./components/LandUse";
import Pesticides from "./components/Pesticides";
import { useCultivarDelete } from "./hooks/useCultivarDelete";
import { useCultivarFormSubmit } from "./hooks/useCultivarFormSubmit";
import { useCultivarSectionRefs } from "./hooks/useCultivarSectionRefs";
import { useCultivarStaticDataLoading } from "./hooks/useCultivarStaticDataLoading";
import { CultivarFormProps, FarmGateFormValues } from "./types/cultivarForm.types";
import { isFarmGateSaved } from "./utils";

import "./styles/CultivarForm.scss";

const CANVAS_PADDING = 25;

const CultivarForm: React.FC<CultivarFormProps> = ({
  supplierId,
  ingredientId,
  farmGateId,
  nodeItem,
  ownerId,
  sourceId,
  lockStatus,
  updateDrawingNode,
  setNodeItem
}) => {
  const {
    sectionRefs,
    cultivationRef,
    fieldRef,
    fertilizerRef,
    pesticideRef,
    irrigationRef,
    fuelUseRef,
    landUseRef,
    notesRef
  } = useCultivarSectionRefs();

  const [activeFarmGateId, setActiveFarmGateId] = useState<string | undefined>(() => {
    const normalizedFarmGateId = normalizeCommentParentId(farmGateId);
    return normalizedFarmGateId ?? undefined;
  });

  const notesParentId = useMemo(
    () => normalizeCommentParentId(activeFarmGateId ?? farmGateId),
    [activeFarmGateId, farmGateId]
  );

  const {
    data: currentFormData,
    status: formFetchStatus,
    fetchStatus: formFetchState
  } = useGetCultivationFormData(activeFarmGateId);

  const isStaticDataLoading = useCultivarStaticDataLoading();

  useEffect(() => {
    const normalizedFarmGateId = normalizeCommentParentId(farmGateId);
    if (normalizedFarmGateId) {
      setActiveFarmGateId(normalizedFarmGateId);
    }
  }, [farmGateId]);

  const [currentDataType, setCurrentDataType] = useState(currentFormData?.dataType || "Supplier");
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [isCultivarValid, setIsCultivarValid] = useState(false);
  const [isFieldDataValid, setIsFieldDataValid] = useState(false);

  const farmGateDefaultValues = useMemo<FarmGateFormValues>(
    () => ({
      farmGateName: currentFormData?.farmGateName ?? ""
    }),
    [currentFormData?.farmGateName]
  );

  const form = useForm<FarmGateFormValues>({ defaultValues: farmGateDefaultValues, mode: "all" });
  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset
  } = form;

  useEffect(() => {
    reset(farmGateDefaultValues, { keepDirtyValues: true });
  }, [farmGateDefaultValues, reset]);

  const updateNodeItem = useCallback(
    (currentNode: any, savedFarmGateId: string, cultivationData?: Record<string, any>) => ({
      ...currentNode,
      data: {
        ...currentNode?.data,
        reference: {
          farmGateId: savedFarmGateId,
          supplierId,
          ingredientId,
          produceType: cultivationData?.produceType,
          originCountry: cultivationData?.originCountry
        }
      }
    }),
    [supplierId, ingredientId]
  );

  const { onSubmitHandler, handleBottomUpdate, isSubmitting } = useCultivarFormSubmit({
    farmGateId: activeFarmGateId ?? farmGateId,
    supplierId,
    ingredientId,
    ownerId,
    sourceId,
    nodeItem,
    currentFormData,
    sectionRefs,
    notesRef,
    form,
    updateNodeItem,
    updateDrawingNode,
    onFarmGateCreated: (createdFarmGateId: string) => {
      const normalizedFarmGateId = normalizeCommentParentId(createdFarmGateId);
      if (normalizedFarmGateId) {
        setActiveFarmGateId(normalizedFarmGateId);
      }
    }
  });

  const { childNodes } = useStore(store => {
    const nodes = Array.from(store.nodeInternals.values()).filter(
      node => node.parentNode === nodeItem?.parentNode
    );
    const rect = getNodesBounds(nodes);

    return {
      minWidth: rect.width + CANVAS_PADDING * 2,
      minHeight: rect.height + CANVAS_PADDING * 2,
      hasChildNodes: nodes.length > 0,
      childNodes: nodes
    };
  }, isCanvasStoreEqual);

  const closeDeleteModal = useCallback(() => setVisibleDeleteModal(false), []);

  const { deleteFarmGateGroup, isDeleting } = useCultivarDelete({
    farmGateId: activeFarmGateId ?? farmGateId,
    nodeItem,
    childNodes,
    notesRef,
    onCloseDeleteModal: closeDeleteModal,
    setNodeItem
  });

  const isLoading = isSubmitting || isDeleting || isStaticDataLoading;
  const isSaveEnabled = isValid && isCultivarValid && isFieldDataValid && !lockStatus;
  const hasSavedFarmGate = isFarmGateSaved(activeFarmGateId ?? farmGateId);
  const sectionsDisabled = !hasSavedFarmGate;

  if (formFetchStatus === "loading" && formFetchState === "fetching") {
    return (
      <div
        className="tw-flex tw-items-center tw-justify-center tw-py-12"
        aria-busy="true"
        aria-live="polite"
      >
        <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div id="cultivarForm" className="tw-relative tw-w-full">
        {isLoading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-background/50">
            <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
          </div>
        )}
        <CommonSplitContainer
          topChild={
            <div className="tw-p-4">
              <div className="userBankDetailsMain cultivarForm">
                <FormProvider {...form}>
                  <FarmGateDetails
                    control={control}
                    farmGateId={activeFarmGateId ?? farmGateId}
                    lockStatus={lockStatus}
                    isValid={isValid}
                    isSaveEnabled={isValid && !lockStatus}
                    handleSubmit={handleSubmit}
                    handleFormSubmit={(values: FarmGateFormValues) =>
                      onSubmitHandler(values, false)
                    }
                    setVisibleDeleteModal={setVisibleDeleteModal}
                  />
                </FormProvider>
                {!hasSavedFarmGate && (
                  <p className="tw-mt-3 tw-text-xs tw-text-muted-foreground tw-italic">
                    Save the Farm Gate Configuration above to access the sections below.
                  </p>
                )}
                <div className="tw-space-y-3 tw-mt-3">
                  <div className="tw-w-full">
                    <CultivarField
                      ref={cultivationRef}
                      currentFormData={currentFormData}
                      currentDataType={setCurrentDataType}
                      disabled={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                      onValidityChange={setIsCultivarValid}
                    />
                  </div>
                  <div className="tw-w-full">
                    <Fields
                      ref={fieldRef}
                      currentFormData={currentFormData}
                      currentDataType={currentDataType}
                      disabled={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                      onValidityChange={setIsFieldDataValid}
                    />
                  </div>
                  <div className="tw-w-full">
                    <Fertilizer
                      ref={fertilizerRef}
                      currentFormData={currentFormData}
                      currentDataType={currentDataType}
                      lockStatus={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                    />
                  </div>
                  <div className="tw-w-full">
                    <Pesticides
                      ref={pesticideRef}
                      currentFormData={currentFormData}
                      currentDataType={currentDataType}
                      lockStatus={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                    />
                  </div>
                  <div className="tw-w-full">
                    <Irrigation
                      ref={irrigationRef}
                      currentFormData={currentFormData}
                      currentDataType={currentDataType}
                      lockStatus={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                    />
                  </div>
                  {/* hide this section using this story NPCW-2877
                  <div className="tw-w-full">
                    <FuelUse
                      ref={fuelUseRef}
                      currentFormData={currentFormData}
                      currentDataType={currentDataType}
                      lockStatus={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                    />
                  </div> */}
                  <div className="tw-w-full">
                    <LandUse
                      ref={landUseRef}
                      currentFormData={currentFormData}
                      currentDataType={currentDataType}
                      lockStatus={lockStatus}
                      sectionsDisabled={sectionsDisabled}
                    />
                  </div>
                </div>
                <div className="tw-mt-3">
                  <NoteAndDocument
                    ref={notesRef}
                    parentId={notesParentId}
                    showSaveButton={false}
                    isDisabled={lockStatus || sectionsDisabled}
                  />
                </div>
              </div>
            </div>
          }
          bottomChild={
            <TailwindBottomButtonView
              saveButtonStatus={true}
              deleteStatus={true}
              cancelButtonStatus={true}
              saveDisable={!isSaveEnabled}
              deleteDisable={!!lockStatus}
              deleteButton={() => setVisibleDeleteModal(true)}
              cancelButton={() => setNodeItem(null)}
              saveButton={handleBottomUpdate}
              saveTitle={activeFarmGateId ?? farmGateId ? "Update" : "Save"}
              deleteButtonVariant="ghost"
              showDeleteIcon
              type="button"
              customHeight={72}
              customBottomContentMain="!tw-px-2 tw-py-3"
            />
          }
          bottomClassName="tw-border-t tw-border-border tw-bg-background"
        />
      </div>

      {visibleDeleteModal && (
        <ConfirmModal
          viewModal={visibleDeleteModal}
          closeModal={closeDeleteModal}
          cancel={closeDeleteModal}
          approve={deleteFarmGateGroup}
          title="Delete Cultivation"
          subTitle="Are you sure you want to delete Cultivation data?"
          style={{ zIndex: 10001 }}
          className="delete-confirmation-modal"
          mountNode={document.body}
        />
      )}
    </>
  );
};

type CanvasStoreSnapshot = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isCanvasStoreEqual(prev: CanvasStoreSnapshot, next: CanvasStoreSnapshot): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export default CultivarForm;
